/**
 * Her money, and the disputes she has raised about it.
 *
 * Her payment is a CLAIM, not a member payout — a different object with a
 * different lifecycle, which is why this does not reuse the member's payment
 * screen. A claim can be HELD, and B7 made a held claim carry the reason it was
 * held rather than failing quietly. That reason is the single most useful thing
 * on this page, so it is rendered in place rather than behind a status chip.
 *
 * DISPUTES ARE RAISED FROM THE PAYMENT, NOT FROM A BLANK FORM. A dispute needs
 * a claim id, and asking her to find one is asking her to do the system's
 * filing. Opening the form from the row means the subject is never ambiguous
 * and never wrong.
 *
 * The two lists live on one page deliberately: "what I am owed" and "what I
 * have queried" are the same question asked twice, and splitting them makes her
 * check two places to find out where a disputed payment got to.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetchJson } from '@/lib/authFetch';
import { PageContainer, PageHeader, PageState } from '@/components/weavein';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AlertCircle, Check, CircleDollarSign, CreditCard, PauseCircle } from 'lucide-react';

interface DoulaPayment {
  claim_id: number;
  claim_number: string | null;
  service_date: string | null;
  charged: number | null;
  paid: number | null;
  status: string;
  payment_date: string | null;
  member_first_name: string | null;
  is_held: boolean;
  held_reason: string | null;
}

interface DoulaCard {
  card_id: number;
  last4: string;
  network: string | null;
  card_type: string | null;
  status: string;
  current_balance: number | null;
  available_balance: number | null;
  issued_on: string | null;
  expires_on: string | null;
}

interface DoulaDispute {
  dispute_id: number;
  dispute_number: string | null;
  claim_id: number | null;
  dispute_type: string;
  status: string;
  raised_at: string;
  resolve_due_at: string | null;
  sla_breached: boolean;
  determination: string | null;
  explanation: string | null;
}

/** The five reasons a doula can give. Kept in step with DISPUTE_TYPES in doula_portal.py. */
const TYPES = ['amount', 'non_receipt', 'denial', 'delay', 'unauthorized'] as const;

function when(v: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { dateStyle: 'medium' });
}

function money(v: number | null): string {
  return v === null || v === undefined ? '—' : `$${v.toFixed(2)}`;
}

export default function DoulaPaymentsPage() {
  const { t } = useTranslation();
  const [payments, setPayments] = useState<DoulaPayment[]>([]);
  // null means she has no card, which is the ORDINARY case — most doulas are
  // paid by transfer. It is a state, not a failure, and the screen says so.
  const [card, setCard] = useState<DoulaCard | null>(null);
  const [disputes, setDisputes] = useState<DoulaDispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const [openFor, setOpenFor] = useState<number | null>(null);
  const [type, setType] = useState<string>('amount');
  const [statement, setStatement] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Settled independently: a doula with no card must still see her
      // payments, and a card that fails to load must not blank the page.
      const [p, d, c] = await Promise.allSettled([
        authFetchJson<DoulaPayment[]>('/api/doula/v1/payments'),
        authFetchJson<DoulaDispute[]>('/api/doula/v1/disputes'),
        authFetchJson<DoulaCard | null>('/api/doula/v1/card'),
      ]);
      if (p.status === 'fulfilled') setPayments(p.value);
      if (d.status === 'fulfilled') setDisputes(d.value);
      if (c.status === 'fulfilled') setCard(c.value ?? null);
      if (p.status === 'rejected') {
        throw p.reason instanceof Error ? p.reason : new Error(String(p.reason));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('doula.paymentsFailed', 'We could not load your payments.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const raise = async (claimId: number) => {
    setBusy(true);
    setError(null);
    try {
      await authFetchJson('/api/doula/v1/disputes', {
        method: 'POST',
        body: JSON.stringify({
          claim_id: claimId,
          dispute_type: type,
          statement: statement.trim() || null,
        }),
      });
      setDone(t('doula.disputeRaised', 'We have this. Someone will look at it and come back to you.'));
      setOpenFor(null);
      setStatement('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('doula.disputeFailed', 'We could not record your dispute.'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <PageState variant="loading" />;

  const disputedClaims = new Set(disputes.map((d) => d.claim_id));

  return (
    <PageContainer>
      <PageHeader
        title={t('doula.myPayments', 'My payments')}
        description={t('doula.myPaymentsDescription', 'What you have been paid, what is on its way, and anything you have queried.')}
      />

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {done && (
        <div className="mb-4 flex items-start gap-2 rounded-md border p-4 text-sm">
          <Check className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{done}</span>
        </div>
      )}

      {card && (
        <section className="mb-6 rounded-md border p-4">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <CreditCard className="h-4 w-4" />
            {t('doula.myCard', 'My card')}
          </h2>
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
            <span className="font-mono text-base">
              {card.network ? `${card.network} ` : ''}•••• {card.last4}
            </span>
            <Badge variant={card.status === 'ACTIVE' ? 'success' : 'secondary'}>
              {card.status === 'ACTIVE'
                ? t('doula.cardActive', 'Active')
                : card.status}
            </Badge>
            {card.available_balance !== null && (
              <span className="text-sm">
                <span className="text-muted-foreground">
                  {t('doula.cardAvailable', 'Available')}{' '}
                </span>
                <span className="tabular-nums font-medium">
                  ${card.available_balance.toFixed(2)}
                </span>
              </span>
            )}
            {card.expires_on && (
              <span className="text-sm text-muted-foreground">
                {t('doula.cardExpires', 'expires {{date}}', { date: when(card.expires_on) })}
              </span>
            )}
          </div>
        </section>
      )}

      {payments.length === 0 ? (
        <div className="rounded-md border p-8 text-center">
          <CircleDollarSign className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm font-medium">{t('doula.noPayments', 'No payments yet.')}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('doula.noPaymentsHint', 'A payment appears here once a visit you logged has been claimed.')}
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {payments.map((p) => (
            <li key={p.claim_id} className="rounded-md border p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-medium">
                    {money(p.paid ?? p.charged)}
                    {p.member_first_name && (
                      <span className="ml-2 text-sm font-normal text-muted-foreground">
                        {t('doula.forMember', 'for {{name}}', { name: p.member_first_name })}
                      </span>
                    )}
                  </p>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    {t('doula.visitOn', 'visit {{date}}', { date: when(p.service_date) })}
                    {p.claim_number ? ` · ${p.claim_number}` : ''}
                  </p>
                </div>
                <Badge variant={p.is_held ? 'warning' : p.paid ? 'success' : 'secondary'}>
                  {p.is_held ? t('doula.held', 'On hold') : p.status}
                </Badge>
              </div>

              {/* The reason it is held, in place. B7 exists so this sentence can
                  be shown instead of a silent failure. */}
              {p.is_held && p.held_reason && (
                <p className="mt-3 flex items-start gap-1.5 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
                  <PauseCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{p.held_reason}</span>
                </p>
              )}

              {openFor === p.claim_id ? (
                <div className="mt-4 space-y-3 border-t pt-3">
                  <div>
                    <Label htmlFor={`type-${p.claim_id}`}>
                      {t('doula.whatIsWrong', 'What is wrong with this payment?')}
                    </Label>
                    <select
                      id={`type-${p.claim_id}`}
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                      className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                    >
                      {TYPES.map((v) => (
                        <option key={v} value={v}>
                          {t(`doula.disputeType.${v}`, v.replace(/_/g, ' '))}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Textarea
                    value={statement}
                    onChange={(e) => setStatement(e.target.value)}
                    rows={3}
                    placeholder={t('doula.disputePlaceholder', 'Tell us what happened — optional.')}
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" disabled={busy} onClick={() => void raise(p.claim_id)}>
                      {t('doula.sendDispute', 'Send')}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setOpenFor(null)}>
                      {t('common.cancel', 'Cancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                !disputedClaims.has(p.claim_id) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-3"
                    onClick={() => {
                      setOpenFor(p.claim_id);
                      setType('amount');
                    }}
                  >
                    {t('doula.queryThis', 'Query this payment')}
                  </Button>
                )
              )}
            </li>
          ))}
        </ul>
      )}

      {disputes.length > 0 && (
        <section className="mt-8">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('doula.myDisputes', 'What you have queried')}
          </h2>
          <ul className="space-y-3">
            {disputes.map((d) => (
              <li key={d.dispute_id} className="rounded-md border p-4 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">
                    {t(`doula.disputeType.${d.dispute_type}`, d.dispute_type.replace(/_/g, ' '))}
                  </span>
                  <Badge variant={d.sla_breached ? 'danger' : 'secondary'}>{d.status}</Badge>
                  {d.dispute_number && (
                    <span className="text-muted-foreground">{d.dispute_number}</span>
                  )}
                </div>
                <p className="mt-1 text-muted-foreground">
                  {t('doula.raisedOn', 'raised {{date}}', { date: when(d.raised_at) })}
                  {d.resolve_due_at
                    ? ` · ${t('doula.answerBy', 'answer due {{date}}', { date: when(d.resolve_due_at) })}`
                    : ''}
                </p>
                {/* Breaching the promise is said plainly rather than left for
                    her to work out from two dates. */}
                {d.sla_breached && (
                  <p className="mt-1 text-red-700">
                    {t('doula.overdue', 'This is past the date we promised you an answer.')}
                  </p>
                )}
                {d.explanation && <p className="mt-2">{d.explanation}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </PageContainer>
  );
}
